import { computed, inject, ref, watch, type Ref } from "vue"
import { storeToRefs } from "pinia"
import { useI18n } from "vue-i18n"
import { UseRootStore } from "@/model/RootStore"
import { useCustomViewStore } from "@/stores/customViewStore"
import { useSearchContextStore } from "@/stores/searchContextStore"
import { analytics } from "@/utils/analytics"
import { ProjectStoreKey } from "@/stores/projectStore"
import { ProjectType, type SearchContext, type SearchContextLayerInput } from "@/graphql"
import type { CustomView } from "@/utils/ApiClient"

type SavedSearch = CustomView | SearchContext

// Layer order reflects toggle history; GraphQL adds empty child lists that live input omits.
function normalizeLayersForComparison(layers: SearchContextLayerInput[]): SearchContextLayerInput[] {
	return layers
		.map(layer => ({
			...layer,
			sublayers: layer.sublayers?.length ? normalizeLayersForComparison(layer.sublayers) : undefined,
		}))
		.sort((a, b) => a.name.localeCompare(b.name))
}

interface UseSavedSearchesOptions {
	filtersCount: Ref<number>
	layersCount: Ref<number>
}

export function useSavedSearches(options: UseSavedSearchesOptions) {
	const { filtersCount, layersCount } = options

	const { t } = useI18n()
	const store = UseRootStore()
	const customViewStore = useCustomViewStore()
	const searchContextStore = useSearchContextStore()
	const { opportunities } = storeToRefs(store)
	const { searchContexts, activeSearchContextId, searchContextsLoading } = storeToRefs(searchContextStore)
	const projectStore = inject(ProjectStoreKey)!()
	const { currentProject } = storeToRefs(projectStore)

	const panelMode = ref<"filters" | "saved">("filters")
	const isNamingSearch = ref(false)
	const isSavingSearch = ref(false)
	const isUpdatingSearch = ref(false)
	const newSearchName = ref("")
	const saveError = ref("")
	const selectedSavedSearchBaseline = ref<string | null>(null)
	const hasUserModifiedActiveSavedSearch = ref(false)

	const savedSearches = computed<SavedSearch[]>(() => searchContexts.value)
	const activeSavedSearchId = computed<string | null>(() => activeSearchContextId.value)
	const isRestoringSavedSearch = computed(() => searchContextsLoading.value)
	const activeSavedSearch = computed<SavedSearch | null>(
		() => savedSearches.value.find(view => view.id === activeSavedSearchId.value) ?? null
	)

	const savedSearchComparisonSignature = computed(() => buildSavedSearchComparisonSignature())
	const isActiveSavedSearchModified = computed(
		() =>
			Boolean(activeSavedSearch.value) &&
			Boolean(selectedSavedSearchBaseline.value) &&
			hasUserModifiedActiveSavedSearch.value &&
			selectedSavedSearchBaseline.value !== savedSearchComparisonSignature.value
	)
	const isUnsavedSearch = computed(() => !activeSavedSearch.value && filtersCount.value > 0)
	const isEmptySearchState = computed(() => !activeSavedSearch.value && !isUnsavedSearch.value)
	const currentSearchTitle = computed(() => {
		if (activeSavedSearch.value) return activeSavedSearch.value.name
		if (isUnsavedSearch.value) return t("custom-views.custom-search")
		return t("custom-views.no-active-filters")
	})
	const currentSearchSummary = computed(() => {
		if (isEmptySearchState.value) return t("custom-views.no-active-filters-description")

		return summaryText(filtersCount.value, layersCount.value, opportunities.value.length)
	})

	const savedSearchProjectId = computed(() =>
		currentProject.value && currentProject.value.type !== ProjectType.Workspace ? currentProject.value.id : null
	)

	watch(activeSavedSearchId, viewId => {
		if (!viewId) {
			hasUserModifiedActiveSavedSearch.value = false
			selectedSavedSearchBaseline.value = null
			return
		}
		if (selectedSavedSearchBaseline.value) return

		// Saved search restored from persisted state: baseline must come from its saved content, not the live filters.
		const view = activeSavedSearch.value
		if (!view) return
		const sourcedBaseline = buildSavedSearchSourceSignature(view)
		if (!sourcedBaseline) {
			// legacy shape we can't hydrate in isolation, fall back to the live snapshot
			setSelectedSavedSearchBaseline()
			return
		}
		selectedSavedSearchBaseline.value = sourcedBaseline
		hasUserModifiedActiveSavedSearch.value = sourcedBaseline !== savedSearchComparisonSignature.value
	})

	function summaryText(filters: number, layers: number, results: number): string {
		return t("custom-views.summary", { filters, layers, results })
	}

	function currentUserProfileId(): string {
		const id = store.currentUser.getInfo()?.id
		if (id == null) throw new Error("Current user id is required")
		return String(id)
	}

	function buildSavedSearchComparisonSignature(): string {
		const input = searchContextStore.searchContextInputFromCurrentSearch({
			name: activeSavedSearch.value?.name ?? "",
			projectId: savedSearchProjectId.value,
		})

		return JSON.stringify({
			layers: input.layers && normalizeLayersForComparison(input.layers),
			views: input.views,
		})
	}

	// same shape as buildSavedSearchComparisonSignature, but built from the view's saved content
	function buildSavedSearchSourceSignature(view: SavedSearch): string | null {
		if (!isSearchContext(view)) return null

		return JSON.stringify({
			layers: normalizeLayersForComparison(view.layers),
			views: view.views,
		})
	}

	function isSearchContext(value: SavedSearch): value is SearchContext {
		return "views" in value && "layers" in value && "display" in value
	}

	function setSelectedSavedSearchBaseline(): void {
		selectedSavedSearchBaseline.value = buildSavedSearchComparisonSignature()
	}

	function markActiveSavedSearchModifiedByUser(): void {
		if (!activeSavedSearch.value) return
		hasUserModifiedActiveSavedSearch.value = true
	}

	function startSave(): void {
		saveError.value = ""
		isNamingSearch.value = true
	}

	function cancelSave(): void {
		isNamingSearch.value = false
		newSearchName.value = ""
		saveError.value = ""
	}

	async function saveCurrentSearch(): Promise<void> {
		if (!newSearchName.value) return

		const name = newSearchName.value
		isSavingSearch.value = true
		saveError.value = ""
		try {
			await searchContextStore.createSearchContextFromCurrentSearch({
				name,
				projectId: savedSearchProjectId.value,
			})
			analytics.savedSearchCreated({
				name,
				userId: store.currentUser.getInfo()?.id ?? null,
			})
			newSearchName.value = ""
			isNamingSearch.value = false
			hasUserModifiedActiveSavedSearch.value = false
			setSelectedSavedSearchBaseline()
		} catch (error) {
			saveError.value = error instanceof Error ? error.message : String(error)
		} finally {
			isSavingSearch.value = false
		}
	}

	async function updateCurrentSearch(): Promise<void> {
		const savedSearch = activeSavedSearch.value
		if (!savedSearch) return

		isUpdatingSearch.value = true
		saveError.value = ""
		try {
			if (!isSearchContext(savedSearch)) return

			await searchContextStore.updateSearchContextFromCurrentSearch(savedSearch.id, {
				projectId: savedSearchProjectId.value,
			})
			hasUserModifiedActiveSavedSearch.value = false
			setSelectedSavedSearchBaseline()
		} catch (error) {
			saveError.value = error instanceof Error ? error.message : String(error)
		} finally {
			isUpdatingSearch.value = false
		}
	}

	function deactivateCurrentSearch(): void {
		cancelSave()
		hasUserModifiedActiveSavedSearch.value = false
		selectedSavedSearchBaseline.value = null
		// also clears a legacy custom view that ActivityPage may have applied independently of this composable
		customViewStore.clearActiveCustomView()
		searchContextStore.clearActiveSearchContext()
	}

	function resetCurrentSearch(): void {
		deactivateCurrentSearch()
		store.resetFiltersToDefault()
		searchContextStore.resetToBaseSearch(savedSearchProjectId.value)
	}

	async function openSavedSearches(): Promise<void> {
		if (!store.isUserLoggedIn) return

		panelMode.value = "saved"
		await searchContextStore
			.fetchSearchContexts({
				userProfileId: currentUserProfileId(),
				projectId: savedSearchProjectId.value,
				force: true,
			})
			.catch((error: unknown) => console.error(error))
	}

	function applySavedSearch(view: SavedSearch): void {
		if (!isSearchContext(view)) return

		hasUserModifiedActiveSavedSearch.value = false
		selectedSavedSearchBaseline.value = null
		searchContextStore.applySavedSearchContext(view, savedSearchProjectId.value)
		setSelectedSavedSearchBaseline()
		panelMode.value = "filters"
	}

	function cancelSavedSearchChanges(): void {
		const savedSearch = activeSavedSearch.value
		if (!savedSearch) return

		cancelSave()
		applySavedSearch(savedSearch)
	}

	return {
		panelMode,
		isNamingSearch,
		isSavingSearch,
		isUpdatingSearch,
		newSearchName,
		saveError,
		savedSearches,
		isRestoringActiveSavedSearch: isRestoringSavedSearch,
		activeSavedSearch,
		isActiveSavedSearchModified,
		isUnsavedSearch,
		isEmptySearchState,
		currentSearchTitle,
		currentSearchSummary,
		resetCurrentSearch,
		deactivateCurrentSearch,
		cancelSavedSearchChanges,
		cancelSave,
		saveCurrentSearch,
		updateCurrentSearch,
		openSavedSearches,
		startSave,
		applySavedSearch,
		markActiveSavedSearchModifiedByUser,
	}
}
